import Mail from 'globals/models/Mail'
import { useState, useEffect } from 'react'
import {
  AddTrackingNumber,
  DestroyParcel,
  MailDetailsActions,
  MailDetailsData,
  MailDetailsDeleteLine,
  MailDetailsScan,
  MailDetailsTitle,
  ModifyTrackingNumber
} from './styles'
import { useTranslation } from 'react-i18next'
import { DeleteOutlineIcon } from 'components/Icons'
import { format } from 'date-fns'
import { Drawer } from 'antd'
import Input from 'components/Input'
import { Formik } from 'formik'
import GraphQLHandler from 'components/GraphQLHandler'
import { useMutation } from '@apollo/client'
import {
  DELETE_MAIL_LINE,
  DeleteMailLineResult,
  MailActionVariables,
  MARK_NPAI,
  MarkNpaiResult,
  PHYSICALLY_DESTROY_MAIL,
  PhysicallyDestroyMailResult,
  UpdateMailResult,
  UPDATE_TRACKING_NUMBER
} from 'pages/ClientDetails/components/MailDetails/mutations'
import { displayErrors, DoMutation } from 'globals/graphql'
import StateIcon from 'components/StateIcon'
import { SetState } from 'globals'
import { TFunction } from 'i18next'
import { Domiciliation } from 'globals/models/Domiciliation'
import pdf from './assets/pdf-file.png'
import { USER_MAIL_ROLLBACK_ACTION } from 'graphql/mails/queries'

interface IProps {
  domiciliation?: Domiciliation
  mail: Mail | null
  visible: boolean
  onClose: () => void
  disableNpai?: boolean
  setShowAddMail?: any
  setIsFromUpdateMail?: SetState<boolean | undefined>
  setSelectedMail?: SetState<Mail | null>
  markAsTracked?: () => void
  refetch?: () => void
}

const MailDetails = (props: IProps) => {
  const {
    domiciliation,
    mail,
    visible,
    onClose,
    setShowAddMail,
    disableNpai,
    setIsFromUpdateMail,
    setSelectedMail,
    markAsTracked,
    refetch
  } = props
  const { t } = useTranslation()
  const [confirmationMessage, setConfirmationMessage] = useState<string>()
  const [modifyTrackingNumber, setModifyTrackingNumber] = useState<boolean>(false)

  const withScanFile: boolean =
    mail?.thumbScanFile !== undefined ||
    (mail?.mailType !== 'colis' &&
      domiciliation?.userMailOption !== 'forwarding' &&
      domiciliation?.userMailOption !== 'provisioning' &&
      domiciliation?.userMailOption !== 'unrestricted_forwarding')

  useEffect(() => {
    setConfirmationMessage(undefined)
    setModifyTrackingNumber(false)
    setSelectedMail && setSelectedMail(mail)
  }, [mail])

  const mailAction = (
    confirmationMessage: string,
    doAction: DoMutation<any, MailActionVariables>,
    trackingNumber?: string | undefined
  ) =>
    doAction({ variables: { userMailId: mail!.id, trackingNumber: trackingNumber } }).then(() =>
      setConfirmationMessage(confirmationMessage)
    )

  const rollback = () =>
    rollbackAction({ variables: { userMailId: mail!.id } }).then(() =>
      setConfirmationMessage(t('mails.actions.confirmations.rollback'))
    )

  const handleSubmit = (doUpdateMail: DoMutation<any, MailActionVariables>, trackingNumber: string | undefined) => {
    if (markAsTracked) {
      markAsTracked()
      onClose()
    } else {
      mailAction(t('mails.tracking-number'), doUpdateMail, trackingNumber)
    }
  }

  const [rollbackAction] = useMutation(USER_MAIL_ROLLBACK_ACTION)
  const disabledAction = domiciliation?.address?.externallyManaged

  const pastParcelDestructionDate = mail?.destructionExpectedDate && new Date(mail.destructionExpectedDate) < new Date()

  return (
    <Drawer visible={visible} width={320} onClose={onClose}>
      <GraphQLHandler
        mutations={{
          markNpai: useMutation<MarkNpaiResult, MailActionVariables>(MARK_NPAI, displayErrors),
          physicallyDestroyMail: useMutation<PhysicallyDestroyMailResult, MailActionVariables>(
            PHYSICALLY_DESTROY_MAIL,
            displayErrors
          ),
          deleteMailLine: useMutation<DeleteMailLineResult, MailActionVariables>(DELETE_MAIL_LINE, displayErrors),
          updateTrackingNumber: useMutation<UpdateMailResult, MailActionVariables>(
            UPDATE_TRACKING_NUMBER,
            displayErrors
          )
        }}
      >
        {({
          mutations: {
            markNpai: [doMarkNpai],
            physicallyDestroyMail: [doPhysicallyDestroyMail],
            deleteMailLine: [doDeleteMailLine],
            updateTrackingNumber: [doUpdateTrackingNumber]
          }
        }) => (
          <>
            {confirmationMessage && mail && displayConfirmationMessage(mail.id, confirmationMessage, t)}

            {!confirmationMessage && mail && (
              <>
                <MailDetailsTitle>{t('mails.number', { number: mail.id })}</MailDetailsTitle>
                {withScanFile && (
                  <MailDetailsScan
                    alt={'Scan ' + mail.id}
                    src={mail?.thumbScanFile || pdf}
                    onClick={() => scanFileView(mail)}
                  />
                )}

                {mail.mailType === 'colis' && (
                  <MailDetailsData>
                    {t('mails.weight')}: <span>{mail.weight} g</span>
                  </MailDetailsData>
                )}

                <MailDetailsData>
                  {t('mails.creation-date')}: <span>{format(new Date(mail.createdAt), 'dd/MM/yyyy')}</span>
                </MailDetailsData>

                {mail.queryDate && (
                  <MailDetailsData>
                    {t('mails.query-date')}: <span>{format(new Date(mail.queryDate), 'dd/MM/yyyy')}</span>
                  </MailDetailsData>
                )}

                {mail.forwardedAt && (
                  <MailDetailsData>
                    {t('mails.forwarding-date')}: <span>{format(new Date(mail.forwardedAt), 'dd/MM/yyyy')}</span>
                  </MailDetailsData>
                )}

                {mail?.trackingNumber && (
                  <MailDetailsData>
                    {t('mails.tracking-number')}: {mail?.trackingNumber}
                    <ModifyTrackingNumber onClick={() => setModifyTrackingNumber(!modifyTrackingNumber)}>
                      modifier
                    </ModifyTrackingNumber>
                    {modifyTrackingNumber && (
                      <AddTrackingNumber marginTop={true}>
                        <Formik
                          initialValues={{}}
                          onSubmit={(e: { trackingNumber?: string }) =>
                            handleSubmit(doUpdateTrackingNumber, e.trackingNumber)
                          }
                        >
                          <Input
                            name='trackingNumber'
                            label={t('mails.tracking-number')}
                            placeholder='Ex: 0189602982'
                            defaultValue={mail?.trackingNumber ? mail.trackingNumber : ''}
                            validateText='Enregistrer'
                            required
                          />
                        </Formik>
                      </AddTrackingNumber>
                    )}
                  </MailDetailsData>
                )}

                {mail.mailType === 'colis' && !mail.trackingNumber && mail?.parcelForwardable && (
                  <AddTrackingNumber>
                    <Formik
                      initialValues={{}}
                      onSubmit={(e: { trackingNumber?: string }) =>
                        handleSubmit(doUpdateTrackingNumber, e.trackingNumber)
                      }
                    >
                      <Input
                        name='trackingNumber'
                        label={t('mails.tracking-number')}
                        placeholder='Ex: 0189602982'
                        validateText='Enregistrer'
                        required
                      />
                    </Formik>
                  </AddTrackingNumber>
                )}
                {!disableNpai && (
                  <>
                    {mail?.parcelForwardable && (
                      <>
                        <MailDetailsActions
                          disabled={disabledAction}
                          onClick={() => mailAction(t('mails.actions.confirmations.mark-npai'), doMarkNpai)}
                        >
                          {t('mails.actions.mark-npai')} {'>'}
                        </MailDetailsActions>

                        <MailDetailsActions
                          disabled={disabledAction}
                          onClick={() => mailAction(t('mails.actions.confirmations.destroy'), doPhysicallyDestroyMail)}
                        >
                          {t('mails.actions.destroy')} {'>'}
                        </MailDetailsActions>
                      </>
                    )}
                    <MailDetailsActions
                      disabled={disabledAction}
                      onClick={() => {
                        setShowAddMail && setShowAddMail(true)
                        setIsFromUpdateMail && setIsFromUpdateMail(true)
                        setSelectedMail && setSelectedMail(mail)
                      }}
                    >
                      {t('mails.actions.modify')} {'>'}
                    </MailDetailsActions>

                    {(mail.forwardedAt || mail.provisionedAt) && (
                      <MailDetailsActions disabled={disabledAction} onClick={rollback}>
                        {t('mails.actions.rollback', {
                          state: mail.forwardedAt ? t('mails.state.forwarded') : t('mails.state.provisioned')
                        })}{' '}
                        {'>'}
                      </MailDetailsActions>
                    )}

                    <MailDetailsDeleteLine
                      disabled={disabledAction}
                      onClick={() => {
                        mailAction(t('mails.actions.confirmations.delete-line'), doDeleteMailLine).then(() => {
                          refetch && refetch()
                        })
                      }}
                    >
                      <span>{t('mails.actions.delete-line')}</span>

                      <DeleteOutlineIcon />
                    </MailDetailsDeleteLine>

                    {mail.mailType === 'colis' && pastParcelDestructionDate && (
                      <DestroyParcel
                        disabled={disabledAction}
                        onClick={() => {
                          mailAction(t('mails.actions.confirmations.deleted-parcel'), doPhysicallyDestroyMail)
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                      >
                        {t('mails.actions.deleted-parcel')}
                        <DeleteOutlineIcon />
                      </DestroyParcel>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </GraphQLHandler>
    </Drawer>
  )
}

const scanFileView = (result: Mail) => {
  if (result?.scanFile) {
    window.open(result.scanFile, '_blank')?.focus()
  }
}

const displayConfirmationMessage = (mailId: number, message: string, t: TFunction) => (
  <>
    <StateIcon type='success' />

    <MailDetailsTitle noMargin>{t('mails.number', { number: mailId })}</MailDetailsTitle>

    {message}
  </>
)

export default MailDetails
